import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import {
  createLogoutRedirector,
  createTrpcAuthHeaders,
} from '@risksmart-app/components/src/utils/trpc/authHeaders';
import { createTrpcAuthRedirectLink } from '@risksmart-app/components/src/utils/trpc/authRedirectLink';
import { createStatusPreservingFetch } from '@risksmart-app/components/src/utils/trpc/statusPreservingFetch';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import superjson from 'superjson';

import {
  getQueryClient,
  TRPCProvider as TanstackTRPCProvider,
} from '../utils/trpc';
import { ThirdPartyAuth0Context } from './ThirdPartyAuth0Context';

interface Props {
  children: ReactNode;
}

/**
 * tRPC v3 provider for the portal (RSP-6570), mirroring
 * `packages/web/src/providers/TrpcProvider.tsx`. Reaches the tRPC API at
 * `REACT_APP_TRPC_API_URL` + `/trpc` — its own var, exactly as web has, NOT
 * `REACT_APP_TENANT_API_URL`. The two coincide only in deployed environments:
 * locally the tenant var points at graphql-engine (`:8080`) while tRPC listens
 * on `:2021`, and on ephemeral the tenant var carries a `/hasura` prefix. Deriving
 * the tRPC URL from the Hasura one made the flag impossible to exercise outside
 * a deployed env and removed the ability to point portal tRPC at a different
 * host from Hasura. Authorises with a Bearer token from the portal's
 * `ThirdPartyAuth0Context`.
 * Mounted unconditionally alongside Apollo so the flag can flip per environment
 * without any provider-tree change; only the hook twins branch on the flag.
 *
 * The transport policy — when a failure may be retried, what a 401 does, how a
 * status survives a body tRPC cannot parse — is the shared one in
 * `@risksmart-app/components/src/utils/trpc/` (COI-159). The portal is a second
 * client on the same browser → tRPC hop, so it gets the same rules by importing
 * them rather than by being kept in step by hand.
 */
export const TrpcProvider: FC<Props> = ({ children }) => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } =
    useRisksmartUser(ThirdPartyAuth0Context);

  const queryClient = getQueryClient();

  // Use refs so the headers function always reads current values
  // without recreating the tRPC client (which causes issues with React Query)
  const getTokenRef = useRef(getAccessTokenSilently);
  const isLoadingRef = useRef(isLoading);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
    isLoadingRef.current = isLoading;
    isAuthenticatedRef.current = isAuthenticated;
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  // Clear cache when auth state changes to prevent stale data from previous sessions
  useEffect(() => {
    if (!isLoading) {
      queryClient.clear();
    }
  }, [isAuthenticated, queryClient, isLoading]);

  const [trpcClient] = useState(() => {
    // ONE per client, shared by the request side (headers) and the response
    // side (the redirect link) so an expired session produces a single /logout
    // navigation however many of the fanned-out requests notice it. `/logout`
    // is the portal's own route too (src/routes/urls.ts), served by the shared
    // LogoutPage, and it is already where ApolloGraphqlProvider sends a
    // respondent whose token cannot be renewed — so this makes the two data
    // paths agree rather than introducing a second mechanism.
    const redirector = createLogoutRedirector();

    return createTRPCClient<AppRouter>({
      links: [
        // First, so it observes every error the chain produces. Drives re-auth
        // when the SERVER rejects the session — the response-side twin of the
        // headers callback below (COI-159).
        createTrpcAuthRedirectLink<AppRouter>({ redirector }),
        httpBatchLink({
          url: `${getEnv('REACT_APP_TRPC_API_URL')}/trpc`,
          maxItems: 1,
          transformer: superjson,
          // The only place in the chain that still holds the real `Response`.
          // @trpc/client parses the body before it builds the error, so a
          // failure with a non-JSON body (the express-jwt 401, any ALB 5xx)
          // otherwise reaches the links with no status on it at all — and the
          // redirect link above would never fire (COI-159).
          fetch: createStatusPreservingFetch(),
          // Aborts rather than sending an unauthenticated request. The portal
          // used to return `{}` from both branches, which sent a request that
          // could only ever be answered 401 — the behaviour COI-897 removed
          // from web. Reporting is the QueryCache's job either way (utils/trpc.ts).
          headers: createTrpcAuthHeaders({
            isLoading: () => isLoadingRef.current,
            isAuthenticated: () => isAuthenticatedRef.current,
            getToken: () => getTokenRef.current(),
            redirector,
          }),
        }),
      ],
    });
  });

  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TanstackTRPCProvider>
    </QueryClientProvider>
  );
};
